import { useLayoutEffect, useState, type RefObject } from "react";

/**
 * Placement for a panel that stands IN PLACE OF a table cell — same top-left,
 * but as wide and as tall as its content needs. Distinct from
 * `dropdown-hooks`'s `DropdownPos`, which places a panel *below* its trigger:
 * an expanded cell should read as the cell having grown, not as a menu that
 * opened under it.
 */
export interface CellOverlayRect {
  top: number;
  left: number;
  width: number;
  /** Space left below `top`; the panel scrolls past it. */
  maxHeight: number;
}

/** Gap kept between the panel and every viewport edge. */
const VIEWPORT_PADDING = 8;
/** Below this the panel is too cramped to be worth opening in place. */
const MIN_PANEL_HEIGHT = 120;

/**
 * Anchors the panel on the cell and grows it right/down, clamped to the
 * viewport: pushed left when it would overflow the right edge, lifted up when
 * the cell sits too close to the bottom for a usable panel.
 *
 * Pure — the hook below owns the DOM reads so this stays unit-testable.
 */
export function computeCellOverlayRect(
  anchor: { top: number; left: number; width: number },
  minWidth: number,
  viewport: { width: number; height: number },
): CellOverlayRect {
  const maxWidth = viewport.width - VIEWPORT_PADDING * 2;
  // `Math.max(anchor.width, …)` on the ceiling: a cell wider than the viewport
  // is a broken layout, but it must not produce a panel NARROWER than the cell
  // it replaces.
  const width = Math.min(
    Math.max(anchor.width, minWidth),
    Math.max(anchor.width, maxWidth),
  );

  let left = anchor.left;
  if (left + width > viewport.width - VIEWPORT_PADDING) {
    left = viewport.width - width - VIEWPORT_PADDING;
  }
  if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;

  let top = anchor.top;
  if (viewport.height - top - VIEWPORT_PADDING < MIN_PANEL_HEIGHT) {
    top = Math.max(
      VIEWPORT_PADDING,
      viewport.height - VIEWPORT_PADDING - MIN_PANEL_HEIGHT,
    );
  }

  return {
    top,
    left,
    width,
    maxHeight: Math.max(
      MIN_PANEL_HEIGHT,
      viewport.height - top - VIEWPORT_PADDING,
    ),
  };
}

/**
 * Keeps a {@link CellOverlayRect} in sync with `anchorRef` while `active`.
 * Measures in a layout effect so the panel lands at its final position in the
 * same paint it first appears in — a panel that flashes at (0,0) first is worse
 * than no panel.
 *
 * Scroll is captured (third arg `true`) because the recap table scrolls in its
 * own container, not on the window.
 */
export function useCellOverlayRect(
  active: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  minWidth: number,
): CellOverlayRect | null {
  const [rect, setRect] = useState<CellOverlayRect | null>(null);

  useLayoutEffect(() => {
    if (!active) {
      setRect(null);
      return;
    }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const box = el.getBoundingClientRect();
      setRect(
        computeCellOverlayRect(
          { top: box.top, left: box.left, width: box.width },
          minWidth,
          {
            width: window.innerWidth,
            height: window.innerHeight,
          },
        ),
      );
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [active, anchorRef, minWidth]);

  return rect;
}
